/**
 * Estado de autenticação centralizado.
 *
 * Protocolo:
 * 1. Ao montar, tenta getMe() — se 200, autenticado (com identidade real).
 * 2. Se 401 OU sem auth em sessionStorage, retorna authed=false (mostra LoginPage).
 * 3. Se outro erro (API down, etc), tratamos como authed=true pra pelo menos
 *    renderizar as páginas (elas exibirão o erro das próprias requests).
 *
 * /api/me é usado em vez de /api/health pra forçar passagem pela auth — sem
 * o header WWW-Authenticate:Basic nos 401, o browser não abre popup nativo.
 */
import { useCallback, useEffect, useState } from "react";
import { AuthRequiredError, clearAuth, getMe, hasAuth } from "../api/client";

export interface AuthState {
  ready: boolean;
  authed: boolean;
  user: string | null;
  onAuthenticated: () => void;
  onAuthRequired: () => void;
  logout: () => void;
}

export function useAuth(): AuthState {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [user, setUser] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((me) => {
        if (!cancelled) {
          setAuthed(true);
          setUser(me.user);
          setReady(true);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof AuthRequiredError || !hasAuth()) {
          setAuthed(false);
        } else {
          setAuthed(true);
        }
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onAuthenticated = useCallback(() => setAuthed(true), []);
  const onAuthRequired = useCallback(() => setAuthed(false), []);
  const logout = useCallback(() => {
    clearAuth();
    setAuthed(false);
    setUser(null);
  }, []);

  return { ready, authed, user, onAuthenticated, onAuthRequired, logout };
}
